import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';
import {Ng4LoadingSpinnerService} from 'ng4-loading-spinner';
import {saveAs} from 'file-saver/FileSaver';

import {UserService} from '@core/ecv-http-services/services/user.service';
import {DialogService} from '../dialog/dialog.service';
import {AlertService} from '@widgets/alert/alert.service';
import {CvParsedModel} from './cv.parsed.model';
import {CvsModel} from './cvs.model';
import {CvsUtils} from './cvs.utils';
import {FileSaverService} from '../shared/file-saver-service';
import {SemanticEnrichmentService} from '../core/ecv-http-services/services/semantic-enrichment.service';
import {AlertUtils} from '@widgets/../shared/AlertUtils';


@Component({
  selector: 'app-cvs',
  templateUrl: './cvs.component.html',
  styleUrls: ['./cvs.component.css']
})
export class CvsComponent implements OnInit {
  @Input() userId: number;
  @Input() userRefNumber: string;
  @Input() enableUploadNewCv = true;
  @Input() enableParseCv = true;
  @ViewChild('fileInput') fileInput: ElementRef;
  cvs: CvsModel[] = [];
  currentIndex = -1;
  @Output() cvParsed = new EventEmitter();
  cvsContentCache: SafeResourceUrl[] = [];
  isRequestInProgress: boolean;

  constructor(private userService: UserService,
              private alertService: AlertService,
              private translateService: TranslateService,
              private dialogService: DialogService,
              private sanitizer: DomSanitizer,
              private fileSaverService: FileSaverService,
              private semanticEnrichmentService: SemanticEnrichmentService,
              private spinnerService: Ng4LoadingSpinnerService) {
    this.isRequestInProgress = false;
  }

  ngOnInit(): void {
    this.loadCvs(this.userId);
  }

  public loadCvs(userId: number) {
    this.userId = userId;
    if (!this.userId) {
      this.cvs = [];
      this.cvsContentCache = [];
      this.currentIndex = -1;
      return;
    }
    this.userService.getCvs(this.userId).subscribe(
      response => {
        this.cvs = response;
        this.cvsContentCache = new Array(this.cvs.length);
        if (this.cvs.length > 0) {
          this.currentIndex = 0;
          this.updateCurrentContent(this.currentIndex);
        }
      });
  }

  public updateCurrentContent(index: number): void {
    if (this.cvsContentCache && this.cvsContentCache[index]) {
      return;
    }
    this.getCvContent(index);
  }

  public getCvContent(index: number) {
    this.userService.getCvContent(this.cvs[index].id).subscribe(
      response => {
        this.cvs[index].content = response.toString();
        const sanitized = this.sanitizer.bypassSecurityTrustResourceUrl(CvsUtils.getFileURL(response.toString()));
        this.cvsContentCache[index] = sanitized;
      }
    );
  }

  public objectSupported(): boolean {
    return !(CvsUtils.detectBrowserSupportsPdfPreview() > 0);
  }

  public uploadCv(event) {
    const reader = new FileReader();
    if (event.target.files && event.target.files.length > 0) {
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        if (file.size === 0) {
          AlertUtils.showAlert(false, 'ALERT.FILE_CV_EMPTY', this.translateService, this.alertService);
          return;
        }
        if (file.size >= CvsUtils.maxCvSize) {
          AlertUtils.showAlert(false, 'ALERT.FILE_CV_TOO_LARGE', this.translateService, this.alertService);
          return;
        }
        if (file.name.length > CvsUtils.maxCvFilenameLength) {
          AlertUtils.showAlert(false, 'ALERT.FILE_CV_NAME_TOO_LONG', this.translateService, this.alertService);
          return;
        }
        reader.readAsDataURL(file);
        reader.onload = () => {
          const cv = new CvsModel();
          cv.content = reader.result.split(',')[1];
          cv.fileName = file.name;
          cv.userId = this.userId;
          this.spinnerService.show();
          this.userService.uploadCv(cv).subscribe(response => {
              Object.assign(cv, response);
              this.cvs.push(cv);
              this.currentIndex = this.cvs.length - 1;
              if (file.name.split('.').pop() !== 'pdf') {
                this.getCvContent(this.currentIndex);
              } else {
                this.cvsContentCache.push(this.sanitizer.bypassSecurityTrustResourceUrl(CvsUtils.getFileURL(cv.content)));
              }
              this.spinnerService.hide();
            }, responseError => {
              this.spinnerService.hide();
            }
          );
        };
      }
    }
  }

  public downloadCV() {
    this.fileSaverService.saveCvFile(this.cvs[this.currentIndex].id);
  }

  public showWikipediaAnchors() {
    this.spinnerService.show();
    this.semanticEnrichmentService.getWikipediaAnchors(this.cvs[this.currentIndex].id).subscribe(
      (response) => {
        this.spinnerService.hide();
        this.dialogService.openWikipediaAnchorsModal(response);
      },
      () => {
        this.spinnerService.hide();
      });
  }

  public parseCV() {
    this.isRequestInProgress = true;
    this.spinnerService.show();
    this.userService.parseCv(this.cvs[this.currentIndex].id).subscribe(
      response => {
        const cvModel = response as CvParsedModel;
        this.enableParseButtonAndHideSpinner();
        this.dialogService.openCvParseModal(cvModel, this);
      },
      error => {
        this.enableParseButtonAndHideSpinner();
      });
  }

  public fileInputClick() {
    this.fileInput.nativeElement.click();
  }

  private enableParseButtonAndHideSpinner() {
    this.isRequestInProgress = false;
    this.spinnerService.hide();
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/cvs/cvs.component.ts